import React from "react";
import PropTypes from "prop-types";
import { SignedOut, useClerk } from "@clerk/clerk-react";

import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";

import BaseDataEntryDialog from "./BaseDataEntryDialog.jsx";
import { track, EVENTS } from "utils/analytics.js";
import { CARD_TOKENS } from "assets/theme/cardTokens.js";
import { ACCOUNT_BENEFITS, ctaPage } from "components/common/user-management/accountOffer.js";

const LoginRequiredDialog = ({
  open,
  handleClose,
  message,
  // Neutral defaults: this dialog also guards the library buttons, where a
  // scenario-specific headline would be wrong.
  title = "Sign in to continue",
  headline = "Keep your work with a free account",
  benefits = ACCOUNT_BENEFITS,
}) => {
  const clerk = useClerk();

  /*
  Close this dialog before opening Clerk's. Clerk renders its modal in its own
  portal, outside this MUI Dialog, and an open MUI Dialog traps focus: it pulls
  focus back into itself whenever it moves out, which would leave the Clerk
  form's fields unusable underneath it.

  The CTA properties match every other CTA_CLICKED: { cta_id, page }. The
  caller's message is not sent; it is a free-text sentence, and the page already
  says where the prompt came from.
  */
  const handleSignUp = () => {
    track(EVENTS.CTA_CLICKED, { cta_id: "auth_dialog_signup", page: ctaPage() });
    handleClose();
    if (clerk && clerk.openSignUp) {
      clerk.openSignUp({ mode: "modal" });
    }
  };

  const handleSignIn = () => {
    track(EVENTS.CTA_CLICKED, { cta_id: "auth_dialog_signin", page: ctaPage() });
    handleClose();
    if (clerk && clerk.openSignIn) {
      clerk.openSignIn({ mode: "modal" });
    }
  };

  return (
    <BaseDataEntryDialog
      open={open}
      onClose={handleClose}
      title={title}
      maxWidth="sm"
      titleId="login-required-dialog-title"
      descriptionId="login-required-dialog-description"
      actions={
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", pt: 0.5 }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            size="small"
            sx={{
              borderColor: "divider",
              color: "text.secondary",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "primary.extraLight",
              },
            }}
          >
            Cancel
          </Button>

          <SignedOut>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="text"
                size="small"
                onClick={handleSignIn}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "text.secondary",
                  "&:hover": {
                    color: "primary.main",
                    backgroundColor: "primary.extraLight",
                  },
                }}
              >
                Sign in
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={handleSignUp}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "6px",
                  backgroundColor: CARD_TOKENS.primary,
                  color: "#ffffff",
                  px: 2,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#5524B8",
                    boxShadow: `0 4px 12px ${CARD_TOKENS.shadow}`,
                  },
                }}
              >
                Create free account
              </Button>
            </Stack>
          </SignedOut>
        </Stack>
      }
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, py: 1 }}>
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: CARD_TOKENS.primaryExtraLight,
            color: CARD_TOKENS.primary,
            boxShadow: `0 8px 20px ${CARD_TOKENS.shadow}`,
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          <BookmarkBorderOutlinedIcon fontSize="medium" />
        </Avatar>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              color: CARD_TOKENS.text,
              mb: 0.5,
              fontSize: "1.0625rem",
            }}
          >
            {headline}
          </Typography>

          <Typography
            id="login-required-dialog-description"
            variant="body2"
            sx={{
              color: "text.secondary",
              lineHeight: 1.6,
              mb: 1.5,
              fontSize: "0.875rem",
            }}
          >
            {message ||
              "Create a free account to save your scenarios and pick up where you left off."}
          </Typography>

          {benefits && benefits.length > 0 && (
            <Stack spacing={0.75} sx={{ mt: 1 }}>
              {benefits.map((benefit) => (
                <Stack
                  key={benefit}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    color: CARD_TOKENS.text,
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                  }}
                >
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: "1rem", color: CARD_TOKENS.success }}
                  />
                  <span>{benefit}</span>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </BaseDataEntryDialog>
  );
};

LoginRequiredDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  message: PropTypes.string,
  title: PropTypes.string,
  headline: PropTypes.string,
  benefits: PropTypes.arrayOf(PropTypes.string),
};

export default LoginRequiredDialog;
